// Invoices API service using HATEOAS style
import { getApiConfiguration } from '../config/apiConfig'

// Types that match the new HATEOAS API structure
export interface Invoice {
  id: string
  href: string
  status: 'Pending' | 'In Collection' | 'Paid' | 'Overdue'
  issued_date: string
  due_date: string
  amount_due: number
  amount_paid: number
  orders: {
    id: string
    href: string
  }
  clinic?: {
    id: string
    href: string
    name: string
  }
  rep?: {
    id: string
    href: string
    name: string
    commission: number
  }
}

export interface InvoicesListResponse {
  items: Invoice[]
  total_items: number
  page_size?: number
  page_number?: number
  page_previous?: string | null
  page_next?: string | null
  href: string
}

export interface InvoicesQueryParams {
  status?: string[]
  issued_date_from?: string
  issued_date_to?: string
  due_date_from?: string
  due_date_to?: string
  page_size?: number
  page_number?: number
  sort?: string[]
}

export const invoicesApi = {
  // HATEOAS-style: Use the href from clinic.invoices.href instead of constructing URL
  async getInvoices(invoicesHref: string, params?: InvoicesQueryParams): Promise<InvoicesListResponse> {
    const config = getApiConfiguration()
    // Build the full URL properly for API calls
    const fullUrl = invoicesHref.startsWith('http')
      ? invoicesHref
      : `${window.location.origin}${config.basePath}${invoicesHref.startsWith('/') ? invoicesHref : '/' + invoicesHref}`
    const url = new URL(fullUrl)
    console.log('HATEOAS Debug - final URL:', url.toString())
    
    // Add query parameters
    if (params) {
      if (params.status) params.status.forEach(s => url.searchParams.append('status', s))
      if (params.issued_date_from) url.searchParams.set('issued_date_from', params.issued_date_from)
      if (params.issued_date_to) url.searchParams.set('issued_date_to', params.issued_date_to)
      if (params.due_date_from) url.searchParams.set('due_date_from', params.due_date_from)
      if (params.due_date_to) url.searchParams.set('due_date_to', params.due_date_to)
      if (params.page_size) url.searchParams.set('page_size', params.page_size.toString())
      if (params.page_number) url.searchParams.set('page_number', params.page_number.toString())
      if (params.sort) params.sort.forEach(s => url.searchParams.append('sort', s))
    }
    
    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch invoices: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    return {
      items: (data.items || []).map((item: any) => ({
        ...item,
        id: item.id!,
        href: item.href!,
      })),
      total_items: data.total_items || 0,
      page_size: data.page_size,
      page_number: data.page_number,
      page_previous: data.page_previous,
      page_next: data.page_next,
      href: data.href || invoicesHref,
    }
  },

  // HATEOAS-style: Use the invoice's own href to get details
  async getInvoice(invoiceHref: string): Promise<Invoice> {
    const config = getApiConfiguration()
    const fullUrl = invoiceHref.startsWith('http')
      ? invoiceHref
      : `${window.location.origin}${config.basePath}${invoiceHref.startsWith('/') ? invoiceHref : '/' + invoiceHref}`
    const url = new URL(fullUrl)
    
    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch invoice: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    return {
      ...data,
      id: data.id!,
      href: data.href!,
    } as Invoice
  },
}